import { Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormGroup, NgForm } from '@angular/forms';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { FreeReducedMealAppService } from '../../free-reduced-meal-app.service';
import { FreeReducedMealAppAdultIncomeSubType } from '../../models/free-reduced-meal-app-adult-income-sub-type.model';
import { FreeReducedMealAppAdultIncome } from '../../models/free-reduced-meal-app-adult-income.model';
import { FreeReducedMealAppAdult } from '../../models/free-reduced-meal-app-adult.model';
import { FreeReducedMealAppPage } from '../../models/free-reduced-meal-app-page.model';
import { FreeReducedMealApp } from '../../models/free-reduced-meal-app.model';

@Component({
  selector: 'frma-adult-income-details',
  templateUrl: './frma-adult-income-details.component.html',
  styleUrls: ['./frma-adult-income-details.component.css']
})
export class FrmaAdultIncomeDetailsComponent implements OnInit {

  static frmaAdultDetailsIncrementor = 1;

  @Input() adultIncomeSubType: FreeReducedMealAppAdultIncomeSubType;
  @Input() adult: FreeReducedMealAppAdult;
  @Input() showMilitaryAdditionalText: boolean;
  @Input() app: FreeReducedMealApp;
  @Input() selectedPage: FreeReducedMealAppPage;

  @ViewChildren('txtAmount', {read: ElementRef}) txtAmount: QueryList<ElementRef>;
  @ViewChildren('ddlFrequency', {read: ElementRef}) ddlFrequency: QueryList<ElementRef>;
  @ViewChildren('txtHours', {read: ElementRef}) txtHours: QueryList<ElementRef>;
  @ViewChildren('ddlPer', {read: ElementRef}) ddlPer: QueryList<ElementRef>;
  @ViewChild('form', { read: NgForm }) form: NgForm;

  localId: number;
  adultIncome: FreeReducedMealAppAdultIncome[] = [];
  additionalQuestionText: string;

  constructor(
    private frmaService: FreeReducedMealAppService
  ) {
    this.localId = FrmaAdultIncomeDetailsComponent.frmaAdultDetailsIncrementor++;
   }

  ngOnInit(): void {
    if (!this.adult.Income) {
      this.adult.Income = [];
    }
    this.adultIncome = this.adult.Income.filter(o => o.AdultIncomeSubTypeID == this.adultIncomeSubType.ID);
  }

  adultHasIncomeClick(hasIncome: boolean) {
    // for new applications we have to track if an answer was selected or not
    if (!this.frmaService.adultIncomeSubTypeClicks[this.adult.ID]) {
      this.frmaService.adultIncomeSubTypeClicks[this.adult.ID] = {};
    }
    this.frmaService.adultIncomeSubTypeClicks[this.adult.ID][this.adultIncomeSubType.ID] = true;
    if (this.adult) {
      if (hasIncome && !this.adult.Income.find(o => o.AdultIncomeSubTypeID == this.adultIncomeSubType.ID)) {
        this.addAdultIncome();
      }
      else if (!hasIncome) {
        for (let i = this.adult.Income.length - 1; i >= 0; i--) {
          if (this.adult.Income[i].AdultIncomeSubTypeID == this.adultIncomeSubType.ID) {
            this.adult.Income.splice(i, 1);
            this.adultIncome = this.adult.Income.filter(o => o.AdultIncomeSubTypeID == this.adultIncomeSubType.ID);
          }
        }
      }
    }
  }

  addAdultIncome() {
    if (this.adult && this.adultIncomeSubType) {
      const newIncome = new FreeReducedMealAppAdultIncome();
      newIncome.AdultIncomeSubTypeID = this.adultIncomeSubType.ID;
      newIncome.AdultID = this.adult.ID;
      if (!this.adult.Income) {
        this.adult.Income = [];
      }
      this.adult.Income.push(newIncome);
      this.adultIncome = this.adult.Income.filter(o => o.AdultIncomeSubTypeID == this.adultIncomeSubType.ID);
    }
  }

  removeClick(adultIncome: FreeReducedMealAppAdultIncome) {
    const i = this.adult.Income.findIndex(o => o == adultIncome);
    if (i >= 0) {
      this.adult.Income.splice(i, 1);
      this.adultIncome = this.adult.Income.filter(o => o.AdultIncomeSubTypeID == this.adultIncomeSubType.ID);
    }
  }

  adultHasIncome(): boolean {
    let hasIncome: boolean = null;
    if (this.adult) {
      if (this.adultIncome && this.adultIncome.length > 0) {
        hasIncome = true;
      }
      else {
        if (this.app.ID > 0 && this.frmaService.pageHasBeenCompleted(this.app, this.selectedPage, null, this.adult)) {
          hasIncome = false;
        }
        else {
          const dict = this.frmaService.adultIncomeSubTypeClicks[this.adult.ID];
          if (dict && dict[this.adultIncomeSubType.ID] == true) {
            hasIncome = false;
          }
        }
      }
    }
    return hasIncome;
  }

  public getElementReference(ci: FreeReducedMealAppAdultIncome, field: string): ElementRef {
    const index = this.adultIncome.findIndex(o => o == ci);
    if (index >= 0 && field) {
      if (field.toLowerCase() == 'amount') {
        if (this.txtAmount.length > index) {
          const ref = this.txtAmount.find((_, i) => i == index);
          return this.txtAmount.find((_, i) => i == index);
        }
      }
      else if (field.toLowerCase() == 'frequency') {
        if (this.ddlFrequency.length > index) {
          return this.ddlFrequency.find((_, i) => i == index);
        }
      }
      else {
        let subIndex = -1;  // we need to calculate the index for the hours and per controls, since there may not be one for every ChilcIncome object
        for (let i = 0; i <= index; i++) {
          if (this.adultIncome[i] && this.adultIncome[i].Frequency.toLowerCase() == 'hourly') {
            subIndex++;
          }
        }
        if (subIndex >= 0) {
          if (field.toLowerCase() == 'hours') {
            if (this.txtHours.length > subIndex) {
              return this.txtHours.find((_, i) => i == subIndex);
            }
          }
          else if (field.toLowerCase() == 'per') {
            if (this.ddlPer.length > subIndex) {
              return this.ddlPer.find((_, i) => i == subIndex);
            }
          }
        }
      }
    }
    return null;
  }

  markAsTouched() {
    Object.keys((<FormGroup>this.form.form).controls).forEach(control => {
      this.form.form.get(control).markAsTouched();
    });
  }

  getNumberMask() {
    return {
        guide: true,
        mask: createNumberMask({
            prefix: '',
            includeThousandsSeparator: false,
            allowDecimal: true,
            decimalLimit: 2
        })
    };
  }
}
